import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Iniciais de um nome para avatar: primeira letra do 1º e do último termo. */
export function iniciais(nome: string): string {
  const partes = (nome ?? "").trim().split(/\s+/).filter(Boolean)
  if (partes.length === 0) return "??"
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase()
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase()
}

export function formatarData(value: string | Date | null | undefined): string {
  if (!value) return "—"
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(
    typeof value === "string" ? new Date(value) : value,
  )
}

export function formatarDataHora(value: string | Date | null | undefined): string {
  if (!value) return "—"
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(typeof value === "string" ? new Date(value) : value)
}

/** Mantém apenas os dígitos do CNPJ, no máximo 14. */
export function somenteDigitosCnpj(value?: string | null): string {
  return (value ?? "").replace(/\D/g, "").slice(0, 14)
}

/** Formata um CNPJ completo (14 dígitos) como 00.000.000/0000-00; senão, devolve o valor. */
export function formatarCnpj(value?: string | null): string {
  if (!value) return ""
  const v = somenteDigitosCnpj(value)
  if (v.length !== 14) return value
  return v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")
}

/** Máscara progressiva de CNPJ (00.000.000/0000-00) para inputs. */
export function mascararCnpj(value?: string | null): string {
  const d = somenteDigitosCnpj(value)
  if (d.length <= 2) return d
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
}

/** Mantém apenas os dígitos do telefone, no máximo 11 (DDD + número). */
export function somenteDigitosTelefone(value?: string | null): string {
  return (value ?? "").replace(/\D/g, "").slice(0, 11)
}

/**
 * Máscara progressiva de telefone brasileiro para inputs:
 * (83) 3333-4444 (fixo) ou (83) 99999-0000 (celular).
 */
export function mascararTelefone(value?: string | null): string {
  const d = somenteDigitosTelefone(value)
  if (!d) return ""
  if (d.length <= 2) return `(${d}`
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

/** Formata um telefone completo (10 ou 11 dígitos); senão, devolve o valor original. */
export function formatarTelefone(value?: string | null): string {
  if (!value) return ""
  const d = somenteDigitosTelefone(value)
  if (d.length !== 10 && d.length !== 11) return value
  return mascararTelefone(d)
}

export function formatarCpf(value?: string | null): string {
  if (!value) return ""
  const v = value.replace(/\D/g, "")
  if (v.length !== 11) return value
  return v.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4")
}

/** Formata valor em reais (BRL) a partir de number/string (BigDecimal). */
export function formatarMoeda(valor?: number | string | null): string {
  if (valor === null || valor === undefined || valor === "") return "-"
  const numero = typeof valor === "string" ? Number(valor) : valor
  if (Number.isNaN(numero)) return "-"
  return numero.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 })
}
